import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Iframe from 'react-iframe';

import {clientActionsApi} from 'api';
import {Button, LoadingDots, PredefinedNotifierType} from 'components';
import {useApiData, useClientSession} from 'hooks';
import {AsyncRequestStatusName, AsyncRequestTypeName, SumaDocumentVerificationSDKData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionStep} from 'types';
import {sessionUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

type Props = {
    sessionId: string;
    participantId: string;
    step: PreSessionStep;
    skipButton?: React.ReactNode;
};

export const AsyncRequestStep: React.FC<Props> = ({sessionId, participantId, step, skipButton}) => {
    const {t} = useTranslation();
    const {session} = useClientSession();
    // Bridges the brief gap between clicking Start/Retry and the new request status arriving via socket.
    const [firing, setFiring] = useState(false);

    const {send: startRequest} = useApiData(
        clientActionsApi.startPreSessionAsyncRequest,
        {notificationType: PredefinedNotifierType.SaveError, onError: () => setFiring(false)},
    );

    const request = useMemo(() => {
        const requests = sessionUtils.getClientParticipant(session)?.asyncRequests ?? [];
        return requests.find((r) => r.type === step.asyncRequest?.typeName);
    }, [session, step.asyncRequest?.typeName]);

    const status = request?.status?.name;

    // SUMA SDK verification is completed entirely inside this iframe: the client performs the
    // check, then SUMA redirects the iframe to our backend (redirect_url), which flips the status.
    // Without rendering it the request stays InProgress forever.
    const sdkUrl = useMemo(() => {
        if (request?.type !== AsyncRequestTypeName.SumaDocumentVerificationSDK) {
            return null;
        }
        return (request.data as SumaDocumentVerificationSDKData)?.url ?? null;
    }, [request]);

    // Once the server-side request exists (any status), status-driven UI takes over.
    useEffect(() => {
        if (status) {
            setFiring(false);
        }
    }, [status]);

    const handleStart = useCallback(() => {
        setFiring(true);
        startRequest({sessionId, participantId}, {stepId: step.id});
    }, [startRequest, sessionId, participantId, step.id]);

    const verifying = firing
        || status === AsyncRequestStatusName.Requested
        || status === AsyncRequestStatusName.InProgress;

    if (status === AsyncRequestStatusName.InProgress && sdkUrl) {
        return (
            <div className='tw-flex tw-flex-col tw-gap-3'>
                <Iframe
                    url={sdkUrl}
                    allow='autoplay *;camera *;microphone *;'
                    height='600px'
                    width='100%'
                    styles={{maxWidth: 800, margin: '0 auto', border: 'none'}}
                />
                {skipButton}
            </div>
        );
    }

    if (verifying) {
        return (
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                <div className='tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-text-sm tw-text-default-dimmed'>
                    <LoadingDots />
                    <span>{t(T_KEYS.IN_PROGRESS)}</span>
                </div>
                {skipButton}
            </div>
        );
    }

    if (status === AsyncRequestStatusName.Failed) {
        return (
            <div className='tw-flex tw-flex-col tw-gap-2'>
                <p className='tw-text-sm tw-text-alert tw-m-0'>{t(T_KEYS.ASYNC_REQUEST_FAILED)}</p>
                <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                    <div className='tw-flex-1'>
                        <Button color='primary' disabled={verifying} loading={verifying} onClick={handleStart}>
                            {t(T_KEYS.ASYNC_REQUEST_RETRY)}
                        </Button>
                    </div>
                    {skipButton}
                </div>
            </div>
        );
    }

    return (
        <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
            <div className='tw-flex-1'>
                <Button color='primary' disabled={verifying} loading={verifying} onClick={handleStart}>
                    {t(T_KEYS.ASYNC_REQUEST_START)}
                </Button>
            </div>
            {skipButton}
        </div>
    );
};
